"use client"

import { useEffect, useId, useState } from "react"
import { motion } from "framer-motion"
import { cn } from "@/shared/lib/utils"
import { getSimulatedGenerationProgress } from "@/shared/lib/simulated-generation-progress"

/** Detailed loader (ring + %) only after this many ms from generation start; avoids flash for fast starts. */
const CHAT_INLINE_LOADER_SHOW_DELAY_MS = 3000

/** Circular ring + centered %; matches generation feed progress. */
export function SlotProgressRing({
  progress,
  suppressAria,
  statusLabel,
}: {
  progress: number
  /** Multi-slot: only the first slot exposes progressbar semantics. */
  suppressAria?: boolean
  /** Extra context for assistive tech (e.g. backend phase label). */
  statusLabel?: string
}) {
  const gradientId = useId()
  const size = 66
  const stroke = 3.5
  const r = (size - stroke) / 2
  const circumference = 2 * Math.PI * r
  const pct = Math.min(100, Math.max(0, progress))
  const dashOffset = circumference * (1 - pct / 100)
  const rounded = Math.round(pct)
  const cx = size / 2
  const cy = size / 2

  const ariaText =
    statusLabel && statusLabel.length > 0
      ? `${statusLabel} - ${rounded}% complete`
      : `${rounded}% complete`

  return (
    <div
      className="pointer-events-none flex flex-col items-center justify-center"
      role={suppressAria ? undefined : "progressbar"}
      aria-hidden={suppressAria ? true : undefined}
      aria-valuemin={suppressAria ? undefined : 0}
      aria-valuemax={suppressAria ? undefined : 100}
      aria-valuenow={suppressAria ? undefined : rounded}
      aria-valuetext={suppressAria ? undefined : ariaText}
    >
      {/* Glass disc keeps the ring legible over any backdrop or reveal frame */}
      <div className="relative flex size-[82px] items-center justify-center rounded-full bg-black/45 shadow-[0_10px_34px_rgba(0,0,0,0.5)] ring-1 ring-white/[0.1] backdrop-blur-md">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          className="-rotate-90 [filter:drop-shadow(0_0_10px_rgba(29,125,242,0.35))]"
          aria-hidden
        >
          <defs>
            <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1d7df2" />
              <stop offset="55%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#ec4899" />
            </linearGradient>
          </defs>
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            className="stroke-white/[0.10]"
            strokeWidth={stroke}
          />
          <motion.circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={false}
            animate={{ strokeDashoffset: dashOffset }}
            transition={{
              type: "spring",
              stiffness: 118,
              damping: 22,
              mass: 0.38,
            }}
          />
        </svg>
        <span className="absolute text-[13px] font-bold leading-none tabular-nums tracking-tight text-white antialiased">
          {rounded}
          <span className="text-[9px] font-semibold text-white/70">%</span>
        </span>
      </div>
    </div>
  )
}

/**
 * Living placeholder while pixels render: a slow aurora wash in brand tones
 * (brightens as progress advances) plus a periodic light sweep. Visible on
 * pure-black backgrounds and small mobile tiles without shouting.
 */
export function GenerationImageLoaderBackdrop({ progress }: { progress: number }) {
  const t = Math.min(1, Math.max(0, progress / 100))

  return (
    <>
      <div className="absolute inset-0 bg-[#0c0c11]" />

      {/* Aurora wash — slow conic drift; gains presence with progress */}
      <motion.div
        className="absolute inset-[-45%]"
        style={{
          background:
            "conic-gradient(from 180deg at 50% 50%, rgba(29,125,242,0.34), rgba(236,72,153,0.22), rgba(139,92,246,0.30), rgba(20,184,166,0.16), rgba(29,125,242,0.34))",
          opacity: 0.45 + t * 0.35,
        }}
        animate={{ rotate: 360 }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-[#0c0c11]/60" />

      {/* Periodic light sweep */}
      <motion.div
        className="absolute inset-y-[-20%] w-[45%]"
        initial={{ x: "-140%" }}
        animate={{ x: "340%" }}
        transition={{
          duration: 2.6,
          repeat: Infinity,
          ease: [0.4, 0, 0.2, 1],
          repeatDelay: 0.9,
        }}
        aria-hidden
      >
        <div className="h-full w-full rotate-[14deg] bg-gradient-to-r from-transparent via-white/[0.085] to-transparent" />
      </motion.div>

      {/* Soft frame: gentle vignette + top hairline so the tile reads as a surface */}
      <div className="pointer-events-none absolute inset-0 shadow-[inset_0_0_52px_rgba(0,0,0,0.5)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-white/[0.10]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/55 to-transparent" />
    </>
  )
}

function useChatInlineSimulatedProgress(anchorMs: number, backendFloor: number) {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 100)
    return () => window.clearInterval(id)
  }, [])

  const elapsed = (Date.now() - anchorMs) / 1000
  void tick

  return getSimulatedGenerationProgress(elapsed, backendFloor)
}

/** Chat bubble placeholder; same loader treatment as `/generate/image` while the edge function streams progress. */
export function ChatInlineImageGenerationLoader({
  progress,
  statusLabel,
  isUser,
  loaderAnchorMs,
  /** Merged onto the outer wrapper; use to embed inside a parent bubble (e.g. iMessage chrome). */
  outerClassName,
}: {
  progress: number
  statusLabel?: string
  isUser: boolean
  loaderAnchorMs: number
  outerClassName?: string
}) {
  const [showLoaderUi, setShowLoaderUi] = useState(false)

  useEffect(() => {
    setShowLoaderUi(false)
    const elapsed = Date.now() - loaderAnchorMs
    const remaining = CHAT_INLINE_LOADER_SHOW_DELAY_MS - elapsed
    if (remaining <= 0) {
      setShowLoaderUi(true)
      return
    }
    const id = window.setTimeout(() => setShowLoaderUi(true), remaining)
    return () => window.clearTimeout(id)
  }, [loaderAnchorMs])

  // Backend percent is a floor only; simulated progress keeps advancing between sparse updates.
  const backendFloor = progress > 0 ? progress : 0
  const simulated = useChatInlineSimulatedProgress(loaderAnchorMs, backendFloor)
  const displayPercent = simulated.percent
  const displayLabel = statusLabel && statusLabel !== "Starting..." ? statusLabel : simulated.label

  const frameClass =
    "relative w-[280px] max-w-full overflow-hidden border border-border/30 bg-card shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]"

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92, y: 8, filter: "blur(4px)" }}
      animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
      transition={{
        type: "spring",
        stiffness: 180,
        damping: 20,
        mass: 0.8,
      }}
      className={cn(
        "overflow-hidden rounded-2xl",
        isUser ? "rounded-br-md" : "rounded-bl-md bg-card border border-border/50",
        outerClassName
      )}
    >
      {showLoaderUi ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className={frameClass}
          style={{ aspectRatio: "832/1280" }}
        >
          <div className="pointer-events-none absolute inset-0 z-[2]">
            <GenerationImageLoaderBackdrop progress={displayPercent} />
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 18 }}
            className="pointer-events-none absolute inset-0 z-[3] flex flex-col items-center justify-center gap-1 p-3"
          >
            <SlotProgressRing progress={displayPercent} statusLabel={displayLabel} />
            {displayLabel.length > 0 ? (
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="max-w-[220px] truncate text-center text-[10px] leading-tight text-white/55"
              >
                {displayLabel}
              </motion.p>
            ) : null}
          </motion.div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.6 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={frameClass}
          style={{ aspectRatio: "832/1280" }}
          aria-busy="true"
          aria-label="Preparing image"
        />
      )}
    </motion.div>
  )
}
